<template>
  <div class="flex flex-col h-full min-h-full">
    <public-announcement :is-fixed="true" />
    <invitation-notifications v-if="currentUser.id" />
    <div class="template-library__content-wrapper">
      <OnboardingV3BackArrow />
      <div class="relative flex w-full mb-4 border-b border-secondary-20" v-show="!isSearching">
        <div class="flex-grow">
          <page-breadcrumb
            category="workspace"
            :text="'Personal'"
            title="Templates"
            :noBorder="true"
          />
        </div>
        <div class="flex-none" v-if="showPublicCommunity">
          <div>
            <!-- desktop publish presentation button -->
            <aha-button
              class="m-2 mt-4 publish-presentation-btn"
              size="md"
              @click="handleOpenModalPublishPresentations()"
            >
              <span>Publish my own</span>
            </aha-button>
            <!-- mobile publish presentation button -->
          </div>
        </div>
      </div>
      <CategoryList :category-id="null" v-if="!isSearching" />
      <div
        v-if="!isEURegion"
        v-show="isSearching"
      >
        <SwitchTabSearch />
      </div>

      <!-- Loading New: with ab test merge search box-->
      <div
        v-if="currentUser.id"
        :class="{
          'flex-grow flex items-center justify-center mr-4 ml-4 md:px-6': templatesLoading
        }"
      >
        <template v-if="templatesLoading">
          <InlineLoader :additionalClasses="'-mt-8'" />
        </template>
      </div>
      <template-not-found
        v-if="!isLoadingTemplates && (templatesError ||
          !templateCategories || templateCategories.length === 0)"
      />

      <TemplateSearchNoDataMergeSearch
        v-show="!templatesLoading"
        v-if="noResult && isSearching"
        :keyword="searchText"
        @request="clearSearch()"
      />
      <div class="mt-10" v-if="isLoadingTemplates && !templateCategories">
        <TemplateSliderSkeleton class="mb-24" />
        <TemplateSliderSkeleton class="mb-24" />
        <TemplateSliderSkeleton class="mb-24" />
        <TemplateSliderSkeleton />
      </div>
      <div
        class="templates"
        v-if="
          templateCategories
            && templateCategories.length > 0
            && filteredPresentations.length === 0
            && !noResult
            && !isSearching
        "
      >
        <div
          v-for="category in templateCategories"
          :key="category.folderId"
          class="mb-4 slider __slider"
        >
          <h2 class="mb-4 text-lg">
            {{ category.folderName }}
          </h2>
          <div
            :class="
              [
                `slider __prev --${category.folderId}`,
                {
                  hidden: isLoadingTemplates
                }
              ]"
            @click="trackSliderBtn('Back')"
          >
            <aha-icon
              width="12"
              height="18"
              viewBox="0 0 8 14"
              name="arrow-no-tail"
              class="transform rotate-180"
            />
          </div>
          <div
            :class="
              [
                `slider __next --${category.folderId}`,
                {
                  hidden: isLoadingTemplates
                }
              ]"
            @click="trackSliderBtn('Next')"
          >
            <aha-icon
              width="12"
              height="18"
              viewBox="0 0 8 14"
              name="arrow-no-tail"
            />
          </div>
          <TemplateSliderSkeleton v-if="isLoadingTemplates" />
          <swiper
            v-if="!isLoadingTemplates
              && category.presentations && category.presentations.length > 0"
            :ref="`swiper-main`"
            :autoUpdate="false"
            :options="getSliderOptions(category)"
          >
            <swiper-slide
              v-for="presentation in category.presentations"
              :key="`${presentation.id} - ${category.id}`"
            >
              <TemplateCard
                :presentation="presentation"
                :where="category.folderName"
                templateLocation="template library"
              />
            </swiper-slide>
          </swiper>
        </div>
        <div class="slider __slider" v-if="!isEURegion">
          <h2 class="mb-4 text-lg">
            Newly Added
          </h2>
          <div
            :class="{
              'slider __prev --newly-added': true,
              hidden: isLoadingNewlyAdded,
            }"
            @click="trackSliderBtn('Back')"
          >
            <aha-icon name="arrow-no-tail" class="transform rotate-180" />
          </div>
          <div
            :class="{
              'slider __next --newly-added': true,
              hidden: isLoadingNewlyAdded,
            }"
            @click="trackSliderBtn('Next')"
          >
            <aha-icon name="arrow-no-tail" />
          </div>
          <TemplateSliderSkeleton v-if="isLoadingNewlyAdded || getNewlyAddedError" />
          <swiper
            ref="swiper-newly-added"
            v-else
            :autoUpdate="false"
            :options="getSliderOptions({ folderId: 'newly-added', folderName: 'Newly added' })"
            v-show="newlyAddedCommunityTemplatesQualified
              && newlyAddedCommunityTemplatesQualified.length > 0"
          >
            <swiper-slide
              v-for="presentation in newlyAddedCommunityTemplatesQualified"
              :key="presentation.id"
            >
              <TemplateCard
                :presentation="presentation"
                where="Newly added"
                templateLocation="template library"
              />
            </swiper-slide>
          </swiper>
        </div>
      </div>

      <div
        class="mt-8 templates"
        v-show="!isLoadingTemplates && !templatesLoading"
        v-if="filteredPresentations && filteredPresentations.length > 0"
      >
        <div class="templates-container">
          <TemplateCard
            class="mb-6"
            v-for="presentation in filteredPresentations"
            :key="presentation.id"
            :presentation="presentation"
          />
        </div>

        <div v-if="isLoadmore" class="w-full mb-4 text-center">
          <aha-button @click="loadMore" :submitting="isLoadingMore" :disabled="isLoadingMore">
            Load more...
          </aha-button>
        </div>
      </div>
    </div>
    <ModalPublishPresentations
      @hide="handleCloseModalPublishPresentations()"
      v-if="isModalPublishPresentationsVisible"
      :presentations="notYetPublishPresentations"
      :total="numberOfPresentations"
    />
    <TemplatePreviewPopup
      v-if="previewPopup"
      @hide="hidePreviewPopUp()"
      :presentationShareCode="previewShareCode"
    />
  </div>
</template>

<script>
import _ from 'lodash'

// components
import {
  mapState, mapActions, mapGetters,
} from 'vuex'
import { Swiper, SwiperSlide } from 'vue-awesome-swiper'
import * as Sentry from '@sentry/vue'
import TemplateCard from '@/components-v2/common/template-library/TemplateCard.vue'
import TemplateNotFound from '@/components-v2/common/template-library/TemplateNotFound.vue'
import PublicAnnouncement from '@/components-v2/common/global-notification/PublicAnnouncement.vue'
import InvitationNotifications from '@/components-v2/common/global-notification/InvitationNotifications.vue'
import PageBreadcrumb from '../common/page-breadcrumb/PageBreadcrumb.vue'
import SwitchTabSearch from '@/components-v2/template/SwitchTabSearch.vue'
import TemplateSearchNoDataMergeSearch from '../common/template-library/TemplateSearchNodataMergeSearch.vue'
import ModalPublishPresentations from '../modals/PublishPresentations.vue'
import InlineLoader from '@/components-v2/common/loading/InlineLoader.vue'
import TemplateSliderSkeleton from './TemplateSliderSkeleton.vue'
import OnboardingV3BackArrow from '@/components-v2/common/onboarding/OnboardingV3BackArrow.vue'
import TemplatePreviewPopup from '@/components-v2/template/TemplatePreviewPopup.vue'
import CategoryList from '@/components-v2/template/CategoryList.vue'

// utils
import utils from '@/util'
import SearchApi from '@/api/search'
import CommunityApi from '@/api/community'
import Config from '@/config'
import constants from '@/constant'
import EventBus from '@/eventBus'

import 'swiper/css/swiper.css'

export default {
  components: {
    PageBreadcrumb,
    TemplateCard,
    TemplateNotFound,
    PublicAnnouncement,
    InvitationNotifications,
    SwitchTabSearch,
    TemplateSearchNoDataMergeSearch,
    ModalPublishPresentations,
    InlineLoader,
    Swiper,
    SwiperSlide,
    TemplateSliderSkeleton,
    OnboardingV3BackArrow,
    TemplatePreviewPopup,
    CategoryList,
  },
  data() {
    return {
      searchText: '',
      filteredPresentations: [],
      noResult: false,
      page: 1,
      isLoadingMore: false,
      isLoadmore: false,
      limit: 20,
      templatesLoading: false,
      isModalPublishPresentationsVisible: false,
      notYetPublishPresentations: [],
      numberOfPresentations: 0,
      clickingToScroll: null,
      imageObserver: null,
      previewPopup: false,
      previewShareCode: '',
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
      hasOnboardingV3: (state) => state.indicators.hasOnboardingV3,
    }),
    ...mapGetters('presentationTemplates', [
      'templateCategories',
      'isLoadingTemplates',
      'trendingTemplates',
      'templatesError',
      'newlyAddedCommunityTemplates',
      'isLoadingNewlyAdded',
      'getNewlyAddedError',
    ]),

    isEURegion() {
      return Config.region === constants.euRegion
    },
    isSearching() {
      return this.$route.query.q
    },
    showPublicCommunity() {
      if (!this.currentUser.email || Config.region === constants.euRegion
        || this.currentUser.isDisablePublicTemplate) {
        return false
      }
      return true
    },
    newlyAddedCommunityTemplatesQualified() {
      const ignoreNames = []
      const whiteList = ['General Knowledge Quiz']
      this.templateCategories.forEach((folder) => {
        folder.presentations.forEach((presentation) => {
          ignoreNames.push(presentation.name)
        })
      })
      return this.newlyAddedCommunityTemplates.filter((presentation) => {
        return (!ignoreNames.includes(presentation.name) || whiteList.includes(presentation.name))
      })
    },
  },
  watch: {
    $route: function handleChangeRoute(newRoute) {
      if (newRoute.name === 'TemplateLibrary'
        && newRoute.query.q) {
        // Reset value and queries input
        this.searchText = newRoute.query.q
        this.handleInputChanged()
      } else {
        // Set blank to reload categories, trending default template
        this.filteredPresentations = []
        this.noResult = false
        this.$nextTick(this.initLazyLoad)
      }
    },
    filteredPresentations: function handleChangeFilteredPresentations(filteredPresentations) {
      if (filteredPresentations.length) {
        this.$nextTick(this.initLazyLoad)
      }
    },
    templateCategories: function handleChangeTemplateCategories(templateCategories) {
      if (templateCategories.length) {
        this.$nextTick(this.initLazyLoad)
      }
    },
    newlyAddedCommunityTemplates: function handleUpdate(newlyAddedCommunityTemplates) {
      if (newlyAddedCommunityTemplates.length) {
        this.$nextTick(this.initLazyLoad)
      }
    },
  },
  async mounted() {
    await this.getTemplatesOldAction()
    await this.getNewlyAddedOldAction()
    this.triggerQuerySearch()
    this.triggerPreviewPopUp()
    EventBus.$on('update-library-after-published', (template) => {
      if (template.customThumbnailImage === '_blank') {
        delete template.customThumbnailImage
      }
      this.updatePublishedTemplateInList(template)
    })
  },
  destroyed() {
    EventBus.$off('update-library-after-published')
  },
  unmounted() {
    this.imageObserver.disconnect()
  },
  methods: {
    ...mapActions('presentationTemplates', [
      'getTemplatesOldAction',
      'getNewlyAddedOldAction',
      'updateNewlyAddedAfterPublishAction']),
    initLazyLoad() {
      if (this.imageObserver) {
        this.imageObserver.disconnect()
      }
      const imageContainers = Array.from(document.querySelectorAll('.template-thumbnail.has-thumbnail'))
      if ('IntersectionObserver' in window) {
        this.imageObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const image = entry.target.querySelector('.thumbnail-image-with-skeleton')
              image.src = image.dataset.src
              this.imageObserver.unobserve(image)
            }
          })
        })
        imageContainers.forEach((img) => this.imageObserver.observe(img))
      } else {
        imageContainers.forEach((container) => {
          const img = container.querySelector('.thumbnail-image-with-skeleton')
          img.src = img.dataset.src
        })
      }
    },
    trackSliderBtn(type) {
      this.clickingToScroll = type
    },
    handleInputChanged: _.debounce(async function handleInputChanged() {
      if (!this.templateCategories || !this.searchText) {
        this.filteredPresentations = []
        this.noResult = false
        this.page = 1
        this.isLoadingMore = false
        this.isLoadmore = false
        return
      }
      this.isLoadingMore = false
      this.isLoadmore = false
      this.page = 1
      this.templatesLoading = true // abtestmergesearch loading flag.
      this.handleSearchPresentation()
    }, 300),

    async handleSearchPresentation() {
      const payload = {
        q: this.searchText,
        page: this.page,
        pageSize: this.limit,
      }

      try {
        const resp = await SearchApi.searchPresentation(payload)
        console.log(resp.body.presentations)
        if (!this.searchText) return
        if (this.isLoadingMore) {
          this.filteredPresentations = this.filteredPresentations.concat(resp.body.presentations)
        } else {
          this.filteredPresentations = resp.body.presentations
        }
        if (resp.body.presentations.length < this.limit) {
          this.isLoadmore = false
        } else {
          this.page++
          this.isLoadmore = true
        }
        utils.gaTrackEvent(constants.gaCategories.TEMPLATE_LIBRARY, 'Search Template', null, null, {
          query: this.searchText,
          showingCount: this.filteredPresentations.length,
        })
      } catch (e) {
        Sentry.captureException(e)
      }
      this.isLoadingMore = false
      this.noResult = this.filteredPresentations.length === 0
      this.templatesLoading = false // abtestmergesearch loading flag.
    },

    triggerPreviewPopUp() {
      if (this.$route.query.preview && this.$route.query.preview.length > 1) {
        this.previewPopup = true
        this.previewShareCode = this.$route.query.preview
      }
    },

    triggerQuerySearch() {
      if (this.$route.query.q && this.$route.query.q.length > 1) {
        this.searchText = this.$route.query.q
        if (!this.searchText) {
          this.filteredPresentations = []
          this.noResult = false
          this.page = 1
          this.isLoadingMore = false
          this.isLoadmore = false
          return
        }
        this.isLoadingMore = false
        this.isLoadmore = false
        this.page = 1
        this.templatesLoading = true // abtestmergesearch loading flag.
        this.handleSearchPresentation()
      }
    },

    clearSearch() {
      this.searchText = ''
      this.handleInputChanged()
    },

    async loadMore() {
      if (this.isLoadingMore) return
      this.isLoadingMore = true
      this.handleSearchPresentation()
      utils.gaTrackEvent('Templates', 'Click load more', '')
    },
    async handleOpenModalPublishPresentations() {
      const data = await CommunityApi.getAllNotPublishedPresentation()
      this.notYetPublishPresentations = data.docs || []
      this.numberOfPresentations = data.numberOfPresentations || 0
      this.isModalPublishPresentationsVisible = true
      utils.gaTrackEvent('Template Library', 'Publish to Template Library')
    },
    handleCloseModalPublishPresentations() {
      this.isModalPublishPresentationsVisible = false
    },
    getSliderOptions(category) {
      // return unique options for each slide
      // avoid using prev/next button on 1 slider also triggers the other sliders
      const { folderId: id, folderName } = category
      return {
        slidesPerView: 'auto',
        slidesPerGroup: 1,
        cssMode: true,
        mousewheel: {
          forceToAxis: true,
        },
        spaceBetween: 32,
        navigation: {
          prevEl: `.slider.__prev.--${id}`,
          nextEl: `.slider.__next.--${id}`,
        },
        breakpoints: {
          768: {
            slidesPerGroup: 2,
          },
          960: {
            slidesPerGroup: 3,
          },
        },
        on: {
          setTranslate: _.debounce((translate) => {
            // hack to track scrolling interactions
            // whenever translate changed, it means slider moved
            // either by 1. clicking or 2. scrolling
            // if button is previously clicked, waiting for translate to change
            if (this.clickingToScroll === null) return // initial load, ignore
            if (this.clickingToScroll) {
              utils.gaTrackEvent(constants.gaCategories.TEMPLATE_LIBRARY, `Templates > Carousel > ${this.clickingToScroll}`, folderName)
              this.clickingToScroll = ''
            } else {
              const direction = translate > this.translate ? 'Left' : 'Right'
              this.translate = translate
              utils.gaTrackEvent(constants.gaCategories.TEMPLATE_LIBRARY, `Templates > Carousel > Swipe ${direction}`, folderName)
            }
          }, 200),
        },
      }
    },
    hidePreviewPopUp() {
      this.previewPopup = false
    },
    updatePublishedTemplateInList(template) {
      this.updateNewlyAddedAfterPublishAction(template)
    },
  },
}
</script>

<style lang="scss" scoped>
$thumbnail-ratio: calc(16 / 9);
$card-width: 250px;

@media (max-width: 768px) {
  .input-search-template {
    position: absolute;
    width: 190px;
    right: 0;
    top: 8px;
  }
}

.publish-presentation-btn {
  display: none;
  @media (min-width: 769px) {
    display: inline-flex;
  }
}

.slider {
  &.__slider {
    @apply relative;
    min-height: 220px;
  }

  &.__prev,
  &.__next {
    @apply absolute cursor-pointer bg-base-0 rounded-full w-10 h-10;
    @apply flex justify-center items-center hover:text-primary-100;
    top: 48%;
    z-index: 2;

    &.swiper-button-disabled {
      @apply hidden;
    }
    &.hidden {
      display: none;
    }
  }

  &.__prev {
    left: -20px;
    @media (max-width: 767px) {
      left: -12px;
    }
  }

  &.__next {
    right: -20px;
    @media (max-width: 767px) {
      right: -12px;
    }
  }
}

.swiper-slide {
  width: $card-width;
  padding-bottom: 0.25rem;
  margin-bottom: 1rem;
}

.template-library__content-wrapper {
  @apply relative flex flex-col h-full m-4 mt-6 md:px-6;
  .onboarding__arrow {
    @apply top-4 lg:-left-4 -left-8;
  }
}

</style>
